import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaHome, FaShoppingBag, FaHistory, FaSearch, FaRocket, FaAsterisk, FaCrown, FaGift } from 'react-icons/fa';
import './OrderSuccess.css';

const OrderSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId, amount, isGuest, guestEmail, orderNumber } = location.state || {};

  // Redirect if no order data (direct URL access)
  useEffect(() => {
    if (!orderId && !orderNumber) {
      navigate('/', { replace: true });
    }
  }, [orderId, orderNumber, navigate]);

  // Show loading while redirecting
  if (!orderId && !orderNumber) {
    return (
      <div className="vastre-success-page">
        <div className="vastre-success-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="vastre-spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vastre-success-page">
      <div className="vastre-success-container">
        <motion.div 
          className="vastre-success-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="vastre-success-icon-container">
            <motion.div 
              className="vastre-success-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                duration: 0.5, 
                delay: 0.2,
                type: "spring",
                stiffness: 200
              }}
            >
              <FaCheckCircle />
            </motion.div>
            <motion.div 
              className="vastre-success-rings"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.4
              }}
            >
              <div className="vastre-ring vastre-ring-1"></div>
              <div className="vastre-ring vastre-ring-2"></div>
              <div className="vastre-ring vastre-ring-3"></div>
            </motion.div>
          </div>
          
          <motion.h1 
            className="vastre-success-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Order Confirmed!
          </motion.h1>
          
          <motion.p 
            className="vastre-success-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Thank you for your purchase. Your order has been successfully placed.
          </motion.p>
        </motion.div>

        {/* Order Details Card */}
        <motion.div 
          className="vastre-order-details-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="vastre-card-header">
            <FaCrown className="vastre-header-icon" />
            <h2>Order Details</h2>
          </div>
          
          <div className="vastre-order-info">
            <div className="vastre-info-item">
              <span className="vastre-label">Order Number</span>
              <span className="vastre-value vastre-order-number">{orderNumber || orderId || 'N/A'}</span>
            </div>
            <div className="vastre-info-item">
              <span className="vastre-label">Amount Paid</span>
              <span className="vastre-value vastre-amount">₹{amount ? amount.toLocaleString() : 'N/A'}</span>
            </div>
          </div>
          
          <div className="vastre-confirmation-note">
            <FaGift className="vastre-note-icon" />
            <p>You will receive an email confirmation shortly with your order details and processing timeline.</p>
          </div>
        </motion.div>

        {/* Guest Info Card */}
        {isGuest && (
          <motion.div 
            className="vastre-guest-info-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="vastre-card-header">
              <FaSearch className="vastre-header-icon" />
              <h2>Track Your Order</h2>
            </div>
            
            <div className="vastre-guest-details">
              <div className="vastre-info-item">
                <span className="vastre-label">Email</span>
                <span className="vastre-value">{guestEmail || 'N/A'}</span>
              </div>
              <div className="vastre-info-item">
                <span className="vastre-label">Order Number</span>
                <span className="vastre-value">{orderNumber || orderId || 'N/A'}</span>
              </div>
            </div>
            
            <p className="vastre-guest-note">
              Use these details to track your order on our tracking page.
            </p>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div 
          className="vastre-action-buttons"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {!isGuest && (
            <motion.button 
              className="vastre-btn-secondary"
              onClick={() => navigate('/order-history')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaHistory />
              View Order History
            </motion.button>
          )}
          
          <motion.button 
            className={isGuest ? "vastre-btn-primary" : "vastre-btn-tertiary"}
            onClick={() => navigate('/track-order', { 
              state: { 
                orderNumber: orderNumber || orderId,
                email: isGuest ? guestEmail : undefined
              }
            })}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSearch />
            Track Your Order
          </motion.button>
          
          <motion.button 
            className="vastre-btn-primary"
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaHome />
            Continue Shopping
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default OrderSuccess;